import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Lock } from "lucide-react";

export default function ProductView() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products/${encodeURIComponent(slug)}/view`);
      setData(data);
      setDenied(false);
      setNotFound(false);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else if (e.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <SkeletonView />;
  if (denied) return <BlockedView title="Нет доступа" description="У вас нет активного доступа к этому продукту." />;
  if (notFound || !data) return <BlockedView title="Продукт не найден" description="Возможно, ссылка неверна или продукт был удалён." />;

  const { product, sections } = data;
  return (
    <div className="min-h-screen bg-cream" data-testid="product-view-page">
      <header className="max-w-3xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button
          onClick={() => nav("/account")}
          data-testid="back-to-account-button"
          className="p-2 -ml-2 rounded-full hover:bg-sand/50"
          aria-label="В Мой кабинет"
        >
          <ChevronLeft className="w-5 h-5 text-espresso" />
        </button>
        <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
          Мой доступ
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-4 pb-16">
        <h1 className="font-serif text-4xl leading-tight text-espresso" data-testid="product-title">
          {product.title}
        </h1>
        {product.short_description && (
          <p className="text-lg text-muted-foreground mt-3">{product.short_description}</p>
        )}
        {product.description && (
          <p className="text-base text-espresso/80 mt-4 whitespace-pre-wrap leading-relaxed">
            {product.description}
          </p>
        )}

        {sections.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-10 font-serif italic" data-testid="product-empty">
            Разделы этого продукта появятся в ближайшее время.
          </p>
        ) : (
          <ul className="mt-8 space-y-6" data-testid="product-sections">
            {sections.map((s) => (
              <li key={s.id} data-testid={`section-${s.id}`}>
                <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Раздел</div>
                <h2 className="font-serif text-2xl text-espresso mt-1">{s.title}</h2>
                {s.description && <p className="text-sm text-muted-foreground mt-1">{s.description}</p>}
                {s.materials.length === 0 ? (
                  <p className="mt-3 text-sm text-muted-foreground italic font-serif">Материалы скоро появятся.</p>
                ) : (
                  <ul className="mt-3 space-y-2" data-testid={`section-${s.id}-materials`}>
                    {s.materials.map((m) => (
                      <li key={m.id}>
                        <Link
                          to={`/products/${encodeURIComponent(product.slug || product.id)}/materials/${encodeURIComponent(m.slug || m.id)}`}
                          data-testid={`material-open-${m.id}`}
                          className="block rounded-2xl bg-white border hairline p-4 hover:bg-sand/20 hover:shadow-sm transition min-h-[64px]"
                        >
                          <div className="font-serif text-lg text-espresso">{m.title}</div>
                          {m.short_description && (
                            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {m.short_description}
                            </div>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SkeletonView() {
  return (
    <div className="min-h-screen bg-cream" data-testid="product-view-loading">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="h-6 w-24 bg-sand/40 rounded animate-pulse" />
        <div className="h-10 w-3/4 bg-sand/40 rounded animate-pulse mt-4" />
        <div className="h-40 bg-sand/30 rounded-3xl animate-pulse mt-6" />
      </div>
    </div>
  );
}

function BlockedView({ title, description }) {
  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5" data-testid="product-view-blocked">
      <Lock className="w-10 h-10 text-espresso/40" />
      <p className="font-serif text-2xl text-espresso mt-4">{title}</p>
      <p className="text-sm text-muted-foreground mt-2 max-w-md text-center">{description}</p>
      <Link to="/account" className="chip bg-espresso text-white mt-6 px-5 py-2">В Мой кабинет</Link>
    </div>
  );
}
